import type {
  PropertiesApiParams,
  PropertyActivityFilters,
  RentalRefreshQueueFilters,
  ValuationRefreshQueueFilters,
} from '../types/propertyTypes'

export const propertyKeys = {
  all: ['property'] as const,

  lists: () => [...propertyKeys.all, 'list'] as const,
  list: (filters: PropertiesApiParams) => [...propertyKeys.lists(), filters] as const,

  // Per-queue list keys (lifecycle §4). Each is just a named list() with the
  // queue's server-side filter baked in, so a queue's invalidation is scoped.
  enrichmentQueue: (filters: PropertiesApiParams) =>
    [...propertyKeys.lists(), 'enrichment', filters] as const,
  financialSetupQueue: (filters: PropertiesApiParams) =>
    [...propertyKeys.lists(), 'financial-setup', filters] as const,
  verificationQueue: (filters: PropertiesApiParams) =>
    [...propertyKeys.lists(), 'verification', filters] as const,
  complianceQueue: (filters: PropertiesApiParams) =>
    [...propertyKeys.lists(), 'compliance', filters] as const,
  vaspQueue: (filters: PropertiesApiParams) => [...propertyKeys.lists(), 'vasp', filters] as const,

  details: () => [...propertyKeys.all, 'detail'] as const,
  detail: (id: string) => [...propertyKeys.details(), id] as const,

  masters: () => [...propertyKeys.all, 'masters'] as const,
  markets: () => [...propertyKeys.masters(), 'markets'] as const,
  countries: () => [...propertyKeys.masters(), 'countries'] as const,

  // Tab sub-queries nested under detail
  financials: (id: string) => [...propertyKeys.detail(id), 'financials'] as const,
  // Fees live under financials: an override re-derives the financials row, so
  // invalidating `financials(id)` has to reach the fee list and back again.
  fees: (id: string) => [...propertyKeys.financials(id), 'fees'] as const,
  feeVersions: (id: string, feeDefinitionId: string) =>
    [...propertyKeys.fees(id), 'versions', feeDefinitionId] as const,
  offeringPrice: (id: string) => [...propertyKeys.financials(id), 'offering-price'] as const,
  coOwners: (id: string) => [...propertyKeys.detail(id), 'co-owners'] as const,
  // Whether this property's ownership belongs to a listing request. Nested under
  // `coOwners` so an ownership refetch picks it up, and because the two are only
  // ever read together.
  ownershipSource: (id: string) => [...propertyKeys.coOwners(id), 'source'] as const,
  // POA rows are nested under a specific holder co-owner. `coOwnerPoaLists`
  // is the broad key so a mutation can invalidate every POA list for a holder.
  coOwnerPoaLists: (propertyId: string, coOwnerId: string) =>
    [...propertyKeys.coOwners(propertyId), coOwnerId, 'poa'] as const,
  coOwnerPoaList: (propertyId: string, coOwnerId: string) =>
    [...propertyKeys.coOwnerPoaLists(propertyId, coOwnerId), 'list'] as const,
  coOwnerPoaDetail: (propertyId: string, coOwnerId: string, poaId: string) =>
    [...propertyKeys.coOwnerPoaLists(propertyId, coOwnerId), 'detail', poaId] as const,
  // Aggregate of every holder's POA rows for a property — nested under
  // `coOwners` so invalidating the co-owner list (after an ownership save /
  // POA delete) cascades a refetch here too. Keyed by the holder record-id set
  // so it refires once the co-owners load and their ids are known.
  coOwnerPoasAll: (propertyId: string, coOwnerIds: string[]) =>
    [...propertyKeys.coOwners(propertyId), 'poa-all', coOwnerIds] as const,
  media: (id: string) => [...propertyKeys.detail(id), 'media'] as const,
  documents: (id: string) => [...propertyKeys.detail(id), 'documents'] as const,
  // Nested so any documents invalidation (upload, replace, delete) reaches it.
  supersededDocuments: (id: string) => [...propertyKeys.documents(id), 'superseded'] as const,
  amenities: (id: string) => [...propertyKeys.detail(id), 'amenities'] as const,
  highlights: (id: string) => [...propertyKeys.detail(id), 'highlights'] as const,
  valuations: (id: string) => [...propertyKeys.detail(id), 'valuations'] as const,
  offerings: (id: string) => [...propertyKeys.detail(id), 'offerings'] as const,
  rentalPeriods: (id: string) => [...propertyKeys.detail(id), 'rental-periods'] as const,
  auditLogs: (id: string) => [...propertyKeys.detail(id), 'audit-logs'] as const,
  activity: (id: string, filters: PropertyActivityFilters) =>
    [...propertyKeys.detail(id), 'activity', filters] as const,
  refunds: (id: string) => [...propertyKeys.detail(id), 'refunds'] as const,

  settings: (propertyId?: string) =>
    propertyId
      ? ([...propertyKeys.all, 'settings', propertyId] as const)
      : ([...propertyKeys.all, 'settings'] as const),

  // Nested under lists(): every status change already invalidates the lists,
  // and the counts must follow — most mutations never named this key, so the
  // sidebar badges and pipeline numbers went stale (PV-02 / BUG-012).
  statusCounts: () => [...propertyKeys.lists(), 'status-counts'] as const,
  rentalHistory: (id: string) => [...propertyKeys.detail(id), 'rental-history'] as const,

  // Refresh queues (property-refresh-queues-plan.md). Hierarchical so that
  // a single `invalidateQueries({ queryKey: rentalRefreshQueueLists() })`
  // refreshes every active filter combination after a mutation.
  rentalRefreshQueueLists: () => [...propertyKeys.all, 'rental-refresh-queue'] as const,
  rentalRefreshQueue: (filters: RentalRefreshQueueFilters) =>
    [...propertyKeys.rentalRefreshQueueLists(), filters] as const,
  rentalRefreshCounts: () => [...propertyKeys.all, 'rental-refresh-counts'] as const,

  valuationRefreshQueueLists: () => [...propertyKeys.all, 'valuation-refresh-queue'] as const,
  valuationRefreshQueue: (filters: ValuationRefreshQueueFilters) =>
    [...propertyKeys.valuationRefreshQueueLists(), filters] as const,
  valuationRefreshCounts: () => [...propertyKeys.all, 'valuation-refresh-counts'] as const,
}
